import {
	ComIbmCommerceOrderCalculationCalculationRuleCombineCmd,
	ComIbmCommerceRestStoreHandlerStoreHandlerRemoteConfig,
	ComIbmCommerceRestStoreHandlerStoreHandlerStoreInformation,
	StoreDatabeanResponse,
	StoreFeaturesList,
	StoreStore,
} from './data-contracts';
import { HttpClient, RequestParams, TraceDataType } from './http-client';

import { loggerCan } from '@/data/utils/loggerUtil';
import { logger } from '@/logging/logger';

export class Store<SecurityDataType = unknown> {
	http: HttpClient<SecurityDataType>;

	constructor(http: HttpClient<SecurityDataType>) {
		this.http = http;
	}

	/**
	 * No description
	 *
	 * @tags store
	 * @name RemoteConfigDetail
	 * @summary Gets reomte store configs.
	 * @request GET:/store/{storeId}/remoteConfig
	 * @secure
	 * @response `200` `ComIbmCommerceRestStoreHandlerStoreHandlerRemoteConfig` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `404` `void` The specified resource could not be found.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	remoteConfigDetail = (
		storeId: string,
		query?: {
			/** The response format. If the request has an input body, that body must also use the format specified in "responseFormat". Valid values include json and xml without the quotes. If the responseFormat isn't specified, the accept HTTP header shall be used to determine the format of the response. If the accept HTTP header isn't specified as well, the default response format shall be in json. */
			responseFormat?: string;
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['remoteConfigDetail'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/remoteConfig`,
				params: _params,
				query: query,
				methodName: 'remoteConfigDetail',
			};
			logger.trace(traceData);
		}
		return this.http.request<ComIbmCommerceRestStoreHandlerStoreHandlerRemoteConfig, void>({
			path: `/store/${storeId}/remoteConfig`,
			method: 'GET',
			query: query,
			secure: true,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * No description
	 *
	 * @name SummaryStore
	 * @request SUMMARY:/store/{storeId}/requisition_list/{requisitionListId}
	 * @secure
	 */
	summaryStore = (storeId: string, requisitionListId: string, params: RequestParams = {}) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (loggerCan('trace') && (!this.http.traceDetails || this.http.traceDetails['summaryStore'])) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/requisition_list/${requisitionListId}`,
				params: _params,
				methodName: 'summaryStore',
			};
			logger.trace(traceData);
		}
		return this.http.request<any, any>({
			path: `/store/${storeId}/requisition_list/${requisitionListId}`,
			method: 'SUMMARY',
			secure: true,
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * No description
	 *
	 * @name SummaryStore2
	 * @request SUMMARY:/store/{storeId}/display_tax
	 * @originalName summaryStore
	 * @duplicate
	 * @secure
	 */
	summaryStore2 = (storeId: string, params: RequestParams = {}) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['summaryStore2'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/display_tax`,
				params: _params,
				methodName: 'summaryStore2',
			};
			logger.trace(traceData);
		}
		return this.http.request<any, any>({
			path: `/store/${storeId}/display_tax`,
			method: 'SUMMARY',
			secure: true,
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * @description Gets tax information by the specified query.
	 *
	 * @tags Store
	 * @name StoreFindByQueryTaxInformation
	 * @summary Get tax information by query
	 * @request GET:/store/{storeId}/taxInformation
	 * @secure
	 * @response `200` `ComIbmCommerceOrderCalculationCalculationRuleCombineCmd` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `404` `void` The specified resource could not be found.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	storeFindByQueryTaxInformation = (
		storeId: string,
		query: {
			/** The query name. */
			q: 'getSalesTaxCalculationRule';
			/** The high range. */
			high?: string;
			/** The low range. */
			low?: string;
			/** The storeIdentifier. */
			storeIdentifier?: string;
			/** The usage. */
			usage?: string;
			/** Profile name. Profiles determine the subset of data to be returned by a query. */
			profileName?: 'IBM_Store_Summary';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['storeFindByQueryTaxInformation'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/taxInformation`,
				params: _params,
				query: query,
				methodName: 'storeFindByQueryTaxInformation',
			};
			logger.trace(traceData);
		}
		return this.http.request<ComIbmCommerceOrderCalculationCalculationRuleCombineCmd, void>({
			path: `/store/${storeId}/taxInformation`,
			method: 'GET',
			query: query,
			secure: true,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * @description Gets online store information by the specified query.
	 *
	 * @tags Store
	 * @name StoreFindByQueryOnlineStore
	 * @summary Get store information by query
	 * @request GET:/store/{storeId}/online_store
	 * @secure
	 * @response `200` `StoreStore` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `404` `void` The specified resource could not be found.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	storeFindByQueryOnlineStore = (
		storeId: string,
		query: {
			/** The query name. */
			q: 'findOnlineStoresByRange' | 'findOnlineStore';
			/** The high range. */
			high?: string;
			/** The low range. */
			low?: string;
			/** Profile name. Profiles determine the subset of data to be returned by a query. */
			profileName?: string;
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['storeFindByQueryOnlineStore'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/online_store`,
				params: _params,
				query: query,
				methodName: 'storeFindByQueryOnlineStore',
			};
			logger.trace(traceData);
		}
		return this.http.request<StoreStore, void>({
			path: `/store/${storeId}/online_store`,
			method: 'GET',
			query: query,
			secure: true,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * @description Gets store details by the specified store name and usage.
	 *
	 * @tags Store
	 * @name StoreFindByStoreNameAndUsage
	 * @summary Get store details by name and usage
	 * @request GET:/store/{storeId}/adminLookup
	 * @secure
	 * @response `200` `StoreStore` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `404` `void` The specified resource could not be found.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	storeFindByStoreNameAndUsage = (
		storeId: string,
		query: {
			/** The query name. */
			q: 'findAllByUsage' | 'findByStoreNameAndUsage' | 'findByStoreIdentifier';
			/** The store identifier */
			storeIdentifier?: string;
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'json' | 'xml';
			/** The store name. */
			storeName?: string;
			/** The usage. */
			usage?: string;
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['storeFindByStoreNameAndUsage'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/adminLookup`,
				params: _params,
				query: query,
				methodName: 'storeFindByStoreNameAndUsage',
			};
			logger.trace(traceData);
		}
		return this.http.request<StoreStore, void>({
			path: `/store/${storeId}/adminLookup`,
			method: 'GET',
			query: query,
			secure: true,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * @description Gets top-level resource URLs.
	 *
	 * @tags Store
	 * @name StoreFetchStore
	 * @summary Get top-level resource URLs
	 * @request GET:/store/{storeId}
	 * @secure
	 * @response `200` `ComIbmCommerceRestStoreHandlerStoreHandlerStoreInformation` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `404` `void` The specified resource could not be found.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	storeFetchStore = (
		storeId: string,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['storeFetchStore'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}`,
				params: _params,
				query: query,
				methodName: 'storeFetchStore',
			};
			logger.trace(traceData);
		}
		return this.http.request<ComIbmCommerceRestStoreHandlerStoreHandlerStoreInformation, void>({
			path: `/store/${storeId}`,
			method: 'GET',
			query: query,
			secure: true,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * @description Gets information about a specific store ID. Can only be called after logging in as an integration user. As such, requests must be sent with an integration user cookie or token. An integration user is a user in the RemoteConfigurationReader member group.
	 *
	 * @tags Store
	 * @name StoreFindDataBean
	 * @summary Get store information by store ID
	 * @request GET:/store/{storeId}/databean
	 * @secure
	 * @response `200` `StoreDatabeanResponse` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `404` `void` The specified resource could not be found.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	storeFindDataBean = (
		storeId: string,
		query?: {
			/** Profile name. Profiles determine the subset of data returned by a query. */
			profileName?: string;
			jspStoreDir?: string;
			storeRelationshipTypeName?: string;
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['storeFindDataBean'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/databean`,
				params: _params,
				query: query,
				methodName: 'storeFindDataBean',
			};
			logger.trace(traceData);
		}
		return this.http.request<StoreDatabeanResponse, void>({
			path: `/store/${storeId}/databean`,
			method: 'GET',
			query: query,
			secure: true,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * @description Gets a list of enabled store features.
	 *
	 * @tags Store
	 * @name StoreGetFeatureList
	 * @summary Gets a list of enabled store features.
	 * @request GET:/store/{storeId}/features
	 * @secure
	 * @response `200` `StoreFeaturesList` The request completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `404` `void` The specified resource could not be found.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	storeGetFeatureList = (storeId: string, params: RequestParams = {}) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['storeGetFeatureList'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/features`,
				params: _params,
				methodName: 'storeGetFeatureList',
			};
			logger.trace(traceData);
		}
		return this.http.request<StoreFeaturesList, void>({
			path: `/store/${storeId}/features`,
			method: 'GET',
			secure: true,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
}
